export class Tenant {
	constructor({ tenantId, name, shortCode, settings } = {}) {
		this.tenantId = tenantId;
		this.name = name;
		this.shortCode = shortCode;
		this.settings = new TenantSettings(settings);
	}
}

export class TenantSettings {
	constructor({
		brandImages = new TenantBrandImages(),
		brandName = 'Locumbase Vets',
		brandUrl = 'https://www.locumbase.com',
		brandYoutubeUrl = '',
		brandAdvertImageLeft = '',
		brandAdvertImageRight = '',
		brandAdvert = new TenantBrandAdvert(),
		theme = new TenantTheme(),
		wallet = new Wallet(),
		links = new TenantLinks(),
		workflow = new TenantWorkflow(),
	} = {}) {
		this.brandImages = new TenantBrandImages(brandImages);
		this.brandName = brandName;
		this.brandUrl = brandUrl;
		this.brandYoutubeUrl = brandYoutubeUrl;
		this.brandAdvertImageLeft = brandAdvertImageLeft;
		this.brandAdvertImageRight = brandAdvertImageRight;
		this.brandAdvert = new TenantBrandAdvert(brandAdvert);
		this.links = new TenantLinks(links);
		this.theme = new TenantTheme(theme);
		this.wallet = new Wallet(wallet);
		this.workflow = new TenantWorkflow(workflow);
	}

	set(vuetify) {
		document.title = this.brandName;
		this.theme.set(vuetify);
		this.setFavicon();
		this.loadGTMTag();
		this.loadChat();
	}

	setFavicon() {
		if (!this.brandImages.favicon || !this.brandImages.favicon.auth) return;
		var link = document.querySelector("link[rel~='icon']");
		if (!link) {
			link = document.createElement('link');
			link.rel = 'icon';
			document.head.appendChild(link);
		}
		link.href = this.brandImages.favicon.auth;
	}

	loadChat() {
		if (this.workflow.support.brevo.active) {
			const key = process.env.VUE_APP_BREVO_CONVERSATIONS_CLIENT_KEY;
			this._createBrevoScript(key);
		} else if (this.workflow.support.tawk.active) {
			this._setChatScript('tawk-chat');
		}
	}

	loadGTMTag() {
		const gtmKey = process.env.VUE_APP_GTM_KEY;
		if (gtmKey) {
			this._createGTMScripts(gtmKey);
		}
	}

	_setChatScript(chatFileName) {
		const script = document.createElement('script');
		script.type = 'text/javascript';
		script.src = `js/${chatFileName}.js`;
		document.head.appendChild(script);
	}

	_createBrevoScript(clientKey) {
		window.BrevoConversationsID = clientKey;
		window.BrevoConversations =
			window.BrevoConversations ||
			function () {
				(window.BrevoConversations.q = window.BrevoConversations.q || []).push(arguments);
			};

		const script = document.createElement('script');
		script.async = true;
		script.src = `https://conversations-widget.brevo.com/brevo-conversations.js`;
		document.head.appendChild(script);
	}

	_createGTMScripts(gtmKey) {
		this._createGTMHeadScript(gtmKey);
		this._createGTMBodyScript(gtmKey);
	}

	_createGTMHeadScript(gtmKey) {
		const script = document.createElement('script');
		script.textContent = `
			window.dataLayer = window.dataLayer || [];
			window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
			(function(w,d,s,l,i){
				w[l]=w[l]||[];w[l].push({'gtm.start':
				new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
				j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
				'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
			})(window,document,'script','dataLayer','${gtmKey}');
		`;
		document.head.appendChild(script);
	}

	_createGTMBodyScript(gtmKey) {
		const noscript = document.createElement('noscript');
		const iframe = document.createElement('iframe');
		iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmKey}`;
		iframe.height = '0';
		iframe.width = '0';
		iframe.style.display = 'none';
		iframe.style.visibility = 'hidden';
		noscript.appendChild(iframe);
		document.body.insertBefore(noscript, document.body.firstChild);
	}
}

export class TenantTheme {
	constructor({
		primary = '#FF5F05', //Orange
		secondary = '#4D88A1', //Cyan
		accent = '#009CEF', // Blue
		error = '#F44336', //Red
		anchor = '#FF5F05',
		background = '#FFFFFFF',
	} = {}) {
		this.primary = primary;
		this.secondary = secondary;
		this.accent = accent;
		this.error = error;
		this.anchor = anchor;
		this.background = background;
	}

	set(vuetify) {
		vuetify.framework.theme.themes.light.primary = this.primary;
		vuetify.framework.theme.themes.light.secondary = this.secondary;
		vuetify.framework.theme.themes.light.accent = this.accent;
		vuetify.framework.theme.themes.light.error = this.error;
		vuetify.framework.theme.themes.light.anchor = this.anchor;
		vuetify.framework.theme.themes.light.background = this.background;
	}
}

const iHeaderLogos = {
	app: {
		light: '',
		dark: '',
	},
	auth: {
		light: '',
		dark: '',
	},
};

const iDashboardImage = {
	company: '',
	professional: '',
};

const iFavicon = {
	app: '',
	auth: '',
};

const iTawk = {
	active: true,
};

const iBrevo = {
	active: false,
};

const iAdvert = {
	image: '',
	url: '',
};

export class TenantBrandImages {
	constructor({ headerLogos = iHeaderLogos, dashboardImage = iDashboardImage, favicon = iFavicon } = {}) {
		this.headerLogos = headerLogos;
		this.dashboardImage = dashboardImage;
		this.favicon = favicon;
	}
}

export class TenantBrandAdvert {
	constructor({ left = iAdvert, right = iAdvert } = {}) {
		this.left = left;
		this.right = right;
	}
}

export class Wallet {
	constructor({ show = false } = {}) {
		this.show = show;
	}
}

export class TenantWorkflow {
	constructor({ support = new TenantSupport() } = {}) {
		this.support = new TenantSupport(support);
	}
}

export class TenantSupport {
	constructor({ tawk = iTawk, brevo = iBrevo } = {}) {
		this.tawk = tawk;
		this.brevo = brevo;
	}
}

export class TenantLinks {
	constructor({ faq = '', terms = '', blog = '', contact = '', downloads = '', tutorials = '' } = {}) {
		this.faq = faq;
		this.terms = terms;
		this.blog = blog;
		this.contact = contact;
		this.downloads = downloads;
		this.tutorials = tutorials;
	}
}

export default Tenant;
